import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { vestingCardStyles } from './vestingCardStyles';
import { useTranslation } from 'react-i18next';
import { StakingCardSkeleton } from '../StakingCardSkeleton/StakingCardSkeleton';
import { useDispatch, useSelector } from 'react-redux';
import { TransactionStatus } from '../../models/Transaction';
import { PATH_OF_ROUTES } from '../../router/routes';
import { updateClaimVestingContractAddress } from '../../store/claim/actions/updateClaimVestingContractAddress';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  selectVestingInfoByContractAddress,
  selectVestingInfoLoading
} from '../../store/vestingInfo/vestingInfoSlice';
import { getVestingRowDataList } from './vestingCardHelper';
import { VestingCardRowInfo } from './VestingCardRowInfo';
import { LoadingType } from '../../models/LoadingType';
import { DataBox } from '../DataBox/DataBox';
import Link from '@mui/material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import { AmountDisplay } from '@brickken/web3';
import { ETHER_SCAN_URL } from '../../services/endpoints';
import { useWallet } from '../../hooks/useWallet';

export interface IStakingCardProps {
  vestingAddress: string;
  title: string;
}

export const VestingCard = ({ vestingAddress, title }: IStakingCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { userAddress } = useWallet();
  const isDark = theme.palette.mode === 'dark';
  const vestingInfo = useSelector((state) =>
    selectVestingInfoByContractAddress(state, vestingAddress)
  );
  const isBuyerUser = vestingInfo?.canClaim;
  const classes = vestingCardStyles(theme, isDark, isBuyerUser);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector(selectVestingInfoLoading);
  const isLoading = loading !== LoadingType.Succeeded;
  const canClaimVesting = +vestingInfo?.availableToClaim > 0;
  const userHasClaimed = +vestingInfo?.totalClaimed > 0;

  const handlerClaim = () => {
    dispatch(
      updateClaimVestingContractAddress({
        claim: { status: TransactionStatus.Init },
        vestingContractAddress: vestingAddress
      })
    );
    navigate(`${PATH_OF_ROUTES.CLAIM_WEB3_INTERACTION}`, {
      replace: true,
      state: { from: location }
    });
  };

  return (
    <Paper sx={classes.card}>
      <Grid
        item
        container
        direction='column'
        alignItems='center'
        justifyContent='center'
        rowSpacing={2}
      >
        <Grid
          item
          container
          direction='column'
          alignItems='center'
          justifyContent='center'
        >
          <Grid
            item
            container
            direction='column'
            alignItems='center'
            justifyContent='center'
            columnSpacing={1}
          >
            <Grid
              item
              container
              direction='row'
              alignItems='center'
              justifyContent='space-between'
            >
              <Grid item>
                <Typography variant='h4' sx={classes.text}>
                  {title}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container direction='column' sx={classes.dataContainer}>
              {getVestingRowDataList(vestingInfo).map((rowdata, index) => (
                <VestingCardRowInfo
                  {...rowdata}
                  key={`${vestingAddress}_${index}`}
                  isLoading={isLoading}
                />
              ))}

              {!isLoading ? (
                <Grid
                  item
                  container
                  rowSpacing={1}
                  direction='column'
                  wrap='nowrap'
                  sx={{ minWidth: 'fit-content' }}
                >
                  <Grid
                    item
                    container
                    columnSpacing={1}
                    direction='column'
                    justifyContent='space-between'
                    sx={classes.rowContainerWallet}
                    wrap='nowrap'
                    rowSpacing={1}
                  >
                    {vestingInfo?.availableToClaim && (
                      <Grid item xs={12} sx={{ display: 'flex' }}>
                        <DataBox
                          title={t(
                            'vesting.card_data.data_box.available_to_claim.title'
                          )}
                          content={
                            <AmountDisplay
                              minDecimals={4}
                              maxDecimals={5}
                              text={vestingInfo?.availableToClaim}
                            />
                          }
                          unit={t('vesting.amount.unit.bkn')}
                        />
                      </Grid>
                    )}
                  </Grid>
                  {isBuyerUser && (
                    <Grid
                      item
                      container
                      direction='column'
                      justifyContent='space-between'
                      columnSpacing={1}
                    >
                      <Grid item xs={12}>
                        <Button
                          size='small'
                          variant='contained'
                          sx={classes.button}
                          onClick={() => handlerClaim()}
                          disabled={!canClaimVesting}
                        >
                          {t('vesting.card_data.button.claim')}
                        </Button>
                      </Grid>

                      {userHasClaimed && (
                        <Grid
                          item
                          container
                          sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <>
                            <Grid item xs='auto'>
                              <Link
                                sx={classes.seeTransactionsLink}
                                href={`${ETHER_SCAN_URL}/address/${vestingAddress}?fromaddress=${userAddress}`}
                                target='_blank'
                              >
                                <Typography
                                  variant='subtitle1'
                                  color={theme.palette.primary.main}
                                >
                                  {t(
                                    'vesting.card_data.claim.see_my_transactions'
                                  )}
                                </Typography>
                                <LaunchIcon
                                  sx={classes.seeTransactionsLinkIcon}
                                />
                              </Link>
                            </Grid>
                          </>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Grid>
              ) : (
                <StakingCardSkeleton />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};
